import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Box,
  Flex,
  VStack,
  Button,
  Image,
} from "@chakra-ui/react";
import { PauseButtonTable } from "@root/components/common/PauseButtonTable";
import { AddIcon } from "@chakra-ui/icons";
import { CustomText } from "@root/components/common/components";
import { useTranslation } from "react-i18next";
import { TABLE_STYLES } from "./actionConstants";
import { Paginate } from "@root/components/pagination";
import { PAGINATION_DEFAULTS } from "@root/constants";
import TrashIcon from "@root/assets/icons/trashicon";
import { AssessmentsUploadService } from "@root/services/assessments";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import { ASSESSMENT_TYPE_PARAM } from "@root/constants/common";
import { DeleteAssessmentModal } from "@root/components/modals";
import { PDF } from "@root/webConstants";
import { renderActionButton } from "@root/utils/assessment-action-button";
import { getUploadedAssessmentsTableColumns } from "./tableColumns";
import { ASSESSMENT_STATUS } from "./actionConstants";
import { useAssessmentPollingSetup } from "@root/hooks/useAssessmentPollingSetup";
import { ASSESSMENT_REVIEW_PROCESSING_STATUSES } from "@root/constants/common";
import { getStatusFromUploadedAssessmentStatus } from "@root/utils/risk-assessment-helpers";
import AssessmentCard1 from "@root/assets/images/AssessmentCard1.svg";
import AssessmentCard2 from "@root/assets/images/AssessmentCard2.svg";

export interface UploadedAssessment {
  id: string;
  assessmentName: string;
  date: string;
  status:
    | "pending"
    | "completed"
    | "in_progress"
    | "processing_sources"
    | "failed"
    | "irrelevant_doc";
  fileType?: string;
}

interface UploadedAssessmentsTableProps {
  assessments: UploadedAssessment[];
  onStartAnalysis: (assessment: UploadedAssessment) => void;
  onViewOutput: (assessment: UploadedAssessment) => void;
  onRetry: (assessment: UploadedAssessment) => void;
  pagination?: boolean;
  count?: number;
  pageSize?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  onUpload?: () => void;
  highlightedAssessmentId?: string;
}

const UploadedAssessmentsTable: React.FC<UploadedAssessmentsTableProps> = ({
  assessments,
  onStartAnalysis,
  onViewOutput,
  onRetry,
  pagination = false,
  count = 0,
  pageSize = PAGINATION_DEFAULTS.PAGESIZE,
  page = 1,
  onPageChange,
  onUpload,
  highlightedAssessmentId,
}) => {
  const { t } = useTranslation("common");
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const service = new AssessmentsUploadService();
  const { fetchRiskAssessments } = useRiskAssessmentContext();
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState<UploadedAssessment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localRows, setLocalRows] = useState<UploadedAssessment[]>(
    assessments || []
  );

  // Sync local rows with props and transform status if needed
  useEffect(() => {
    const transformed = (assessments || []).map((assessment: any) => {
      // Transform numeric status to string status if needed
      if (typeof assessment.status === "number" && assessment.status != null) {
        return {
          ...assessment,
          status: getStatusFromUploadedAssessmentStatus(assessment.status),
        };
      }
      return assessment;
    });
    setLocalRows(transformed);
  }, [assessments]);

  // Create a wrapper setter that transforms status from numeric to string
  const setLocalRowsWithStatusTransform = React.useCallback(
    (updater: React.SetStateAction<UploadedAssessment[]>) => {
      setLocalRows((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        // Transform any numeric statuses to string statuses
        return next.map((item: any) => {
          if (typeof item.status === "number" && item.status != null) {
            return {
              ...item,
              status: getStatusFromUploadedAssessmentStatus(item.status),
            };
          }
          return item;
        });
      });
    },
    []
  );

  // Polling setup for non-completed uploaded rows, persists across routes
  useAssessmentPollingSetup({
    items: localRows,
    setItems: setLocalRowsWithStatusTransform,
    processingStatuses: ASSESSMENT_REVIEW_PROCESSING_STATUSES,
    dependencies: [localRows.length],
  });

  // Fixed columns for uploaded assessments as per provided design
  const tableColumns = getUploadedAssessmentsTableColumns(t);

  // Show empty state when there are no assessments
  if (!localRows || localRows.length === 0) {
    return (
      <Box>
        <Flex justify="center" align="center" minH="60vh">
          <VStack spacing={8} align="center">
            {/* Assessment Cards Visualization */}
            <Box position="relative">
              {/* Concentric Rings */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="300px"
                h="300px"
                borderRadius="50%"
                border="1px solid"
                borderColor="gray.200"
                opacity={0.3}
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="200px"
                h="200px"
                borderRadius="50%"
                border="1px solid"
                borderColor="gray.200"
                opacity={0.5}
              />
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                w="100px"
                h="100px"
                borderRadius="50%"
                border="1px solid"
                borderColor="gray.200"
                opacity={0.7}
              />

              {/* Assessment Cards */}
              <VStack spacing={4} align="center">
                <Image src={AssessmentCard1} mb={"-25px"} />
                <Image src={AssessmentCard2} />
                <Image src={AssessmentCard1} mt={"-75px"} />
              </VStack>
            </Box>

            {/* Empty State Message */}
            <VStack spacing={4} align="center">
              <CustomText
                fontSize="24px"
                fontWeight="700"
                textColor="#1A202C"
                text={t("assessments.noUploadedAssessments")}
              />
              <CustomText
                fontSize="16px"
                textColor={"#6C757D"}
                text={t("assessments.pleaseCreateAssessment")}
              />
            </VStack>

            {/* Upload Assessment Button */}
            {onUpload && (
              <Button
                w={"200px"}
                bg="#005EB8"
                color="white"
                borderRadius="8px"
                p={"16px"}
                fontSize="14px"
                fontWeight="500"
                onClick={onUpload}
                _hover={{
                  bg: "#005EB8",
                }}
              >
                {t("assessments.upload.button")}{" "}
                <AddIcon boxSize={"12px"} ml={"8px"} />
              </Button>
            )}
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer
        bg={bgColor}
        borderRadius={TABLE_STYLES.borderRadius}
        border="1px solid"
        borderColor={borderColor}
        overflowX="auto"
      >
        <Table variant="simple" size="md">
          <Thead bg={TABLE_STYLES.headerBg}>
            <Tr>
              {tableColumns?.map((column) => (
                <Th
                  key={column?.key}
                  fontSize={TABLE_STYLES?.headerFontSize}
                  fontWeight={TABLE_STYLES?.headerFontWeight}
                  color={TABLE_STYLES?.headerTextColor}
                  py={TABLE_STYLES?.cellPadding}
                  width={column?.width}
                >
                  {column?.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {localRows?.map((assessment: any) => {
              const isHighlighted =
                highlightedAssessmentId &&
                String(assessment?.id) === String(highlightedAssessmentId);
              return (
                <Tr
                  key={assessment?.id}
                  bg={isHighlighted ? "rgba(0, 94, 184, 0.08)" : undefined}
                  _hover={{ bg: TABLE_STYLES?.hoverBg }}
                >
                  <Td py={TABLE_STYLES?.cellPadding}>
                    <CustomText
                      fontSize="14px"
                      text={assessment.assessmentName}
                    />
                  </Td>
                  <Td py={TABLE_STYLES?.cellPadding} width="280px">
                    <CustomText
                      fontSize="14px"
                      text={
                        assessment?.mimeType?.includes(PDF)
                          ? PDF?.toUpperCase()
                          : "N/A"
                      }
                    />
                  </Td>
                  <Td py={TABLE_STYLES?.cellPadding} width="280px">
                    <CustomText fontSize="14px" text={assessment.date} />
                  </Td>
                  <Td py={TABLE_STYLES?.cellPadding} width="180px">
                    {renderActionButton({
                      assessment,
                      onStartAnalysis,
                      onViewOutput,
                      onRetry,
                      t,
                    })}
                  </Td>
                  <Td py={TABLE_STYLES?.cellPadding} width="60px">
                    {assessment?.status !== ASSESSMENT_STATUS.IN_PROGRESS &&
                    assessment?.status !==
                      ASSESSMENT_STATUS.PROCESSING_SOURCES ? (
                      <Box
                        as="button"
                        onClick={() => {
                          setSelected(assessment);
                          setIsModalOpen(true);
                        }}
                      >
                        <TrashIcon />
                      </Box>
                    ) : (
                      <PauseButtonTable
                        onClick={() => {}}
                        showSpinner={false}
                      />
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      {pagination && onPageChange && (
        <Flex justify="center" mt={6}>
          <Paginate
            count={count}
            pageSize={pageSize}
            page={page}
            onPageChange={onPageChange}
          />
        </Flex>
      )}

      <DeleteAssessmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isLoading={deleting}
        assessmentName={selected?.assessmentName}
        onConfirm={async () => {
          if (!selected) return;
          try {
            setDeleting(true);
            await service.deleteAssessment(baseUrl, selected.id);
            setIsModalOpen(false);
            setSelected(null);
            await fetchRiskAssessments({
              limit: PAGINATION_DEFAULTS.PAGESIZE,
              offset: (page - 1) * pageSize,
              assessmentType: ASSESSMENT_TYPE_PARAM.UPLOADED,
            });
          } finally {
            setDeleting(false);
          }
        }}
      />
    </Box>
  );
};

export default UploadedAssessmentsTable;
